// API service for Swipey.ai authentication endpoints
import { getEfTransactionId } from './everflow'
import { getTrackingParams } from './tracking'
import { setStorage, getStorage, setAuth, isAnnualPlan } from './storage'
import { applyPostSignupFlow, getSelectedCharacterSlug } from './postSignupRedirect'

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL

const API_BASE_URL = `${SWIPEY_BASE_URL}/api/v1`

const API_KEY = process.env.REACT_APP_API_KEY
// Voice label mapping (UI value → API label)
const voiceLabelMap = {
  'Voice 3': 'Teen (18+)',
  'Voice 5': 'Mature',
  // Voice 4 (Soft) doesn't exist in API → returns null intentionally
}

// Ethnicity mapping
const ethnicityLabelMap = {
  Arab: 'Mid.Eastern',
}

// Relationship mapping
const relationshipLabelMap = {
  stepSis: 'Step Sister',
  stepMom: 'Step Mom',
  girlfriend: 'Girlfriend',
  sexFriend: 'Fuck Buddy',
}

// Attributes map cache
let attributesMap = {}
let attributesLoaded = false

// Common headers for all API requests
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'x-api-key': API_KEY,
})

// External-click-id keys cache. The list of keys (e.g. ['twclid','gclid','fbclid','ttclid'])
// is owned by the backend; we fetch it once per page load and reuse it for every auth call.
let externalClickIdKeys = null
let externalClickIdKeysPromise = null

// Snapshot the landing-URL query params into sessionStorage on module load.
// We read externalClickIds from this snapshot — NOT from localStorage `utm_data`
// (which merges across visits and leaks stale click-ids), and NOT from
// `window.location.search` at submit time (SPA navigation strips it).
// sessionStorage clears when the tab closes → each tab starts fresh.
const LANDING_URL_PARAMS_KEY = 'landing_url_params'
;(() => {
  if (typeof window === 'undefined') return
  try {
    if (sessionStorage.getItem(LANDING_URL_PARAMS_KEY)) return
    const snapshot = {}
    new URLSearchParams(window.location.search).forEach((value, key) => {
      snapshot[key] = value
    })
    sessionStorage.setItem(LANDING_URL_PARAMS_KEY, JSON.stringify(snapshot))
  } catch {}
})()

// Fetch the list of click-id keys the backend wants us to forward. Cached for the page lifetime;
// in-flight requests are deduped via `externalClickIdKeysPromise`. Failures / timeouts fall back
// to [] so auth + OAuth redirects can't be blocked by this endpoint — externalClickIds gets sent as {}.
const EXTERNAL_CLICK_ID_KEYS_TIMEOUT_MS = 3000

const fetchExternalClickIdKeys = async () => {
  if (Array.isArray(externalClickIdKeys)) return externalClickIdKeys
  if (externalClickIdKeysPromise) return externalClickIdKeysPromise

  externalClickIdKeysPromise = (async () => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), EXTERNAL_CLICK_ID_KEYS_TIMEOUT_MS)
      : null
    try {
      const response = await fetch(`${API_BASE_URL}/marketing/external-click-id-keys`, {
        method: 'GET',
        headers: getHeaders(),
        signal: controller?.signal,
      })
      if (!response.ok) {
        externalClickIdKeys = []
        return externalClickIdKeys
      }
      const data = await response.json()
      externalClickIdKeys = Array.isArray(data) ? data : []
      return externalClickIdKeys
    } catch (err) {
      console.warn('[externalClickIds] keys fetch failed, falling back to []', err)
      externalClickIdKeys = []
      return externalClickIdKeys
    } finally {
      if (timeoutId) clearTimeout(timeoutId)
      externalClickIdKeysPromise = null
    }
  })()

  return externalClickIdKeysPromise
}

// Build the externalClickIds object the backend expects: for each key returned by
// /marketing/external-click-id-keys, pull the value from this tab's landing-URL snapshot.
// Source rationale (see LANDING_URL_PARAMS_KEY comment above):
//   - NOT localStorage.utm_data → leaks click-ids from previous visits
//   - NOT window.location.search at submit time → SPA navigation strips it
export const getExternalClickIds = async () => {
  const keys = await fetchExternalClickIdKeys()
  let landingParams = {}
  try {
    const raw = sessionStorage.getItem(LANDING_URL_PARAMS_KEY)
    if (raw) landingParams = JSON.parse(raw) || {}
  } catch {
    landingParams = {}
  }

  return keys.reduce((acc, key) => {
    const value = landingParams[key]
    acc[key] = value == null || value === '' ? null : value
    return acc
  }, {})
}

// Generic API request function
const apiRequest = async (endpoint, method = 'GET', body = null) => {
  try {
    const config = {
      method,
      headers: getHeaders(),
    }

    if (body) {
      config.body = JSON.stringify(body)
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, config)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`)
    }

    return data
  } catch (error) {
    throw error
  }
}

// Load attributes from API
export const loadCharacterAttributes = async () => {
  if (attributesLoaded) return attributesMap

  try {
    const response = await fetch(`${API_BASE_URL}/characters-attributes`, {
      method: 'GET',
      headers: getHeaders(),
    })

    if (!response.ok) {
      throw new Error('❌ Unable to load attributes')
    }

    const result = await response.json()

    result.forEach(attr => {
      if (!attr.type || !attr.label || !attr._id) return
      const type = attr.type.toLowerCase()
      if (!attributesMap[type]) attributesMap[type] = {}

      if (attr.style && attr.gender) {
        const key = `${attr.label.trim()}_${attr.style}_${attr.gender}`
        attributesMap[type][key] = attr._id
        // Voice: style is irrelevant for our UI — also store under plain label
        if (type === 'voice') {
          attributesMap[type][attr.label.trim()] = attr._id
        }
      } else {
        attributesMap[type][attr.label.trim()] = attr._id
      }
    })

    console.log('✅ Attributes loaded. Ethnicity keys:', Object.keys(attributesMap.ethnicity || {}))
    console.log('✅ Relationship keys:', Object.keys(attributesMap.relationship || {}))
    console.log('✅ Voice keys:', Object.keys(attributesMap.voice || {}))
    console.log('✅ Age keys:', Object.keys(attributesMap.age || {}))

    attributesLoaded = true
    return attributesMap
  } catch (err) {
    return {}
  }
}

// Helper function to get attribute ID with style and gender matching
export const getAttributeId = (type, label, userStyle, userGender) => {
  if (!attributesMap[type] || !label) return null

  // Handle voice mapping
  if (type === 'voice' && voiceLabelMap[label]) {
    label = voiceLabelMap[label]
  }

  // Handle ethnicity mapping
  if (type === 'ethnicity' && ethnicityLabelMap[label]) {
    label = ethnicityLabelMap[label]
  }

  // Handle relationship mapping
  if (type === 'relationship' && relationshipLabelMap[label]) {
    label = relationshipLabelMap[label]
  }

  // First try to find with exact style and gender match
  if (userStyle && userGender) {
    const styleKey = `${label.trim()}_${userStyle}_${userGender}`
    if (attributesMap[type][styleKey]) {
      return attributesMap[type][styleKey]
    }
  }

  // Fallback to plain label
  return attributesMap[type][label.trim()] || null
}

const apiToCamelCaseMap = {
  ethnicity: 'ethnicity',
  age: 'ageAttribute',
  eyecolor: 'eyeColor',
  hairstyle: 'hairStyle',
  haircolor: 'hairColor',
  bodytype: 'bodyType',
  breastsize: 'breastSize',
  buttsize: 'buttSize',
  personality: 'personality',
  occupation: 'occupation',
  hobbies: 'hobbies',
  relationship: 'relationship',
  voice: 'defaultVoice',
}

// Convert quiz selections to API format
export const convertSelectionsToApiFormat = async selections => {
  // Ensure attributes are loaded
  await loadCharacterAttributes()

  const userAnswers = {
    gender: 'Girls',
    style: selections?.welcome === 'Anime' ? 'Anime' : 'Realistic',
    attributes: {},
  }

  const { style, gender } = userAnswers
  console.log('🎨 convertSelectionsToApiFormat — style:', style, 'gender:', gender)

  // Step1: ethnicity, age
  if (selections?.step1) {
    if (selections.step1.ethnicity) {
      const id = getAttributeId('ethnicity', selections.step1.ethnicity, style, gender)
      console.log(`🔍 ethnicity lookup: "${selections.step1.ethnicity}" + style=${style} + gender=${gender} → id=${id}`)
      if (id) userAnswers.attributes[apiToCamelCaseMap.ethnicity] = id
    }
    if (selections.step1.age) {
      userAnswers.attributes[apiToCamelCaseMap.age] = selections.step1.age
    }
  }

  // Step2: body attributes
  if (selections?.step2) {
    const step2Map = {
      bodyType: 'bodytype',
      breastSize: 'breastsize',
      buttSize: 'buttsize',
    }
    Object.entries(step2Map).forEach(([key, apiKey]) => {
      if (selections.step2[key]) {
        const id = getAttributeId(apiKey, selections.step2[key], style, gender)
        if (id) userAnswers.attributes[apiToCamelCaseMap[apiKey]] = id
      }
    })
  }

  // Step3: hair, eyes
  if (selections?.step3) {
    const step3Map = {
      hairColor: 'haircolor',
      hairStyle: 'hairstyle',
      eyeColor: 'eyecolor',
    }
    Object.entries(step3Map).forEach(([key, apiKey]) => {
      if (selections.step3[key]) {
        const id = getAttributeId(apiKey, selections.step3[key], style, gender)
        if (id) userAnswers.attributes[apiToCamelCaseMap[apiKey]] = id
      }
    })
  }

  // Step4: personality, voice
  if (selections?.step4) {
    if (selections.step4.personality) {
      const ids = Array.isArray(selections.step4.personality)
        ? selections.step4.personality.map(p => getAttributeId('relationship', p, style, gender)).filter(Boolean)
        : [getAttributeId('relationship', selections.step4.personality, style, gender)].filter(Boolean)

      if (ids.length > 0) userAnswers.attributes[apiToCamelCaseMap.relationship] = ids.length > 1 ? ids : ids[0]
    }

    if (selections.step4.voice) {
      const voiceId = getAttributeId('voice', selections.step4.voice, style, gender)
      if (voiceId) userAnswers.attributes[apiToCamelCaseMap.voice] = voiceId
    }
  }

  return userAnswers
}

// Create model-params from selections
export const createModelParams = async selections => {
  const userAnswers = await convertSelectionsToApiFormat(selections)

  const modelParamsObj = {
    formBody: {
      style: userAnswers.style,
      gender: userAnswers.gender,
      attributes: { ...userAnswers.attributes },
      defaultVoice: null,
    },
  }

  console.log('🚀 Final model params:', JSON.stringify(modelParamsObj, null, 2))

  const json = JSON.stringify(modelParamsObj)
  const base64 = btoa(unescape(encodeURIComponent(json)))

  return base64
}

// Decorate the paywall URL with extra params (theme, selected_slug, plans,
// etc.) and forward UTM-style query params from the current page. The
// `modelParams` argument is intentionally ignored — sending model-params to
// xDate paywall makes useHandleAttributesInQueryParams stash createAi state
// in localStorage, which then routes post-payment to /generating-ai instead
// of /chats/{selected_slug}. We strip it defensively from any source.
export const addModelParamsToUrl = (baseUrl, _modelParams, additionalParams = {}) => {
  const url = new URL(baseUrl)

  // Add additional parameters
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value)
    }
  })

  // Forward current page's query params (utm, etc.) — but never model-params.
  const currentUrlParams = new URLSearchParams(window.location.search)
  for (const [key, value] of currentUrlParams.entries()) {
    if (key === 'model-params') continue
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value)
    }
  }

  // Belt-and-suspenders: strip model-params if backend slipped it into the
  // returned redirectUrl. Without this, post-payment goes to /generating-ai.
  url.searchParams.delete('model-params')

  return url.toString()
}

// UTM data management - now using our tracking system
export const collectAndStoreUTMData = () => {
  // Initialize tracking if not already done
  if (typeof window !== 'undefined' && window.initTracking) {
    window.initTracking()
  }

  const trackingParams = getTrackingParams()

  // Also get current URL params directly as fallback
  const currentUrlParams = new URLSearchParams(window.location.search)
  const currentParams = {}
  for (const [key, value] of currentUrlParams.entries()) {
    currentParams[key] = value
  }
  const utmObj = {
    ...trackingParams,
    ...currentParams, // Include current URL params as fallback
    path: '/paywall',
  }

  // Store UTM data using our storage utility
  setStorage('utm_data', utmObj)
  return utmObj
}

// Enhanced auth callback handler with model params
export const handleAuthCallback = async (selections = null) => {
  const params = new URLSearchParams(window.location.search)
  const authToken = params.get('auth_token')
  const authType = params.get('auth_type')

  if (authToken && authType) {
    const utm = getStorage('utm_data') || {}
    const paywallUrl = new URL(`${SWIPEY_BASE_URL}/paywall`)

    if (selections?.welcome) {
      const typeValue = selections.welcome === 'Anime' ? 'anime' : 'realistic'
      paywallUrl.searchParams.set('type', typeValue)
    }

    paywallUrl.searchParams.set('auth_token', authToken)
    paywallUrl.searchParams.set('auth_type', authType)
    // Tell xDate paywall the user came from a lander and which chat to
    // redirect to after payment (`/chats/${selected_slug}`).
    paywallUrl.searchParams.set('from_landing', 'true')
    paywallUrl.searchParams.set('selected_slug', getSelectedCharacterSlug())

    // Add type parameter based on selections style
    if (selections?.welcome) {
      const typeValue = selections.welcome === 'Anime' ? 'anime' : 'realistic'
      paywallUrl.searchParams.set('type', typeValue)
    }

    // Add UTM params
    Object.entries(utm).forEach(([key, value]) => {
      if (value) paywallUrl.searchParams.set(key, value)
    })

    // Add model params if selections are available
    // Intentionally NOT setting `model-params` — see comment in paywall.js
    // buildPaywallUrl. xDate caches model-params and overrides selected_slug
    // routing, sending the user to /generating-ai instead of /chats/{slug}.

    // /12 route forces annual; otherwise honor the GB
    // createsexfriends-post-signup-redirect flag persisted at signup time.
    const annualByRoute = isAnnualPlan()
    const annualByFlag = !annualByRoute && localStorage.getItem('postSignupRedirect') === 'annual'
    if (annualByRoute || annualByFlag) {
      paywallUrl.searchParams.set('plans', 'annual')
    }
    // One-shot consumption: clear so next OAuth callback re-derives from
    // current GB value instead of a stale 'annual' from a prior flag state.
    localStorage.removeItem('postSignupRedirect')

    window.location.replace(
      applyPostSignupFlow(paywallUrl.toString(), { authToken }),
    )
    return true // Indicates callback was handled
  }
  return false // No callback to handle
}

// Account creation API with model params
export const createAccount = async (email, password, selections = null) => {
  const ef_transaction_id = getEfTransactionId()
  const payload = { email, password, ef_transaction_id }

  const data = await apiRequest('/auth/account', 'POST', payload)

  if (data.success && data.redirectUrl && selections) {
    try {
      const modelParams = await createModelParams(selections)
      data.redirectUrl = addModelParamsToUrl(data.redirectUrl, modelParams)
      // Model params added successfully
    } catch (err) {
      // Error adding model params
    }
  }

  return data
}

// Enhanced pre-lander account creation API with model params
export const createPreLanderAccount = async (email, password, selections = null, ab_testing) => {
  const utm = getStorage('utm_data') || {}
  const ef_transaction_id = getEfTransactionId()
  const externalClickIds = await getExternalClickIds()

  const data = await apiRequest('/auth/pre-lander', 'POST', {
    ...utm,
    email,
    password,
    ef_transaction_id,
    ab_testing,
    lp_source: 'createaitrans',
    externalClickIds,
  })

  if (data.message && data.message.includes('User with this email already exists')) {
    return {
      success: false,
      error: 'Email already used',
    }
  }

  // If successful, decorate the backend-returned redirectUrl with the
  // params xDate paywall needs to route post-payment to /chats/{slug}.
  // Crucially: STRIP `model-params` if the backend put it there —
  // otherwise xDate caches it under `createAiParamsAfterPaywall` and
  // routes to /generating-ai instead of /chats/{slug}.
  // See xDate/frontend/src/hooks/usePaymentSuccessHandlers.ts (premiumOnClose).
  if (data.success && data.redirectUrl) {
    try {
      const url = new URL(data.redirectUrl)
      url.searchParams.delete('model-params')
      url.searchParams.set('from_landing', 'true')
      url.searchParams.set('selected_slug', getSelectedCharacterSlug())
      const theme = ab_testing && ab_testing['createsexfriends-paywall-theme']
      if (theme) url.searchParams.set('theme', theme)
      if (isAnnualPlan()) url.searchParams.set('plans', 'annual')
      data.redirectUrl = url.toString()
    } catch (err) {
      console.error('Error decorating pre-lander redirectUrl', err)
    }
  }


  return data
}

// Enhanced pre-lander login API with model params
export const loginPreLander = async (email, password, selections = null) => {
  const utm = getStorage('utm_data') || {}
  const ef_transaction_id = getEfTransactionId()

  const payload = {
    ...utm,
    email,
    password,
    ef_transaction_id,
  }

  const data = await apiRequest('/auth/pre-lander', 'POST', payload)

  // Add model params to any redirect URL if selections are available
  if (data.success && data.redirectUrl && selections) {
    const modelParams = await createModelParams(selections)
    data.redirectUrl = addModelParamsToUrl(data.redirectUrl, modelParams, {
      auth_token: data.authToken,
      auth_type: 'user',
    })
    // Enhanced login redirect URL with model params
  }

  return data
}

// Enhanced complete login flow handler with model params
export const handleLoginFlow = async (email, password, selections, ab_testing) => {
  try {
    const utm = getStorage('utm_data') || {}
    const ef_transaction_id = getEfTransactionId()
    const externalClickIds = await getExternalClickIds()

    const data = await apiRequest('/auth/pre-lander/login', 'POST', {
      ...utm,
      email,
      password,
      ef_transaction_id,
      ab_testing,
      externalClickIds,
    })

    const authToken = data.authToken || data.token
    const user = data

    if (authToken) {
      setAuth(authToken)

      const isSubscribed = user?.isSubscribed === true
      const isNewAccount = window.location.pathname.includes('/newaccount')
      const isPw = window.location.pathname.includes('/pw')
      const isHalloween = window.location.pathname.includes('/halloween')

      if (isSubscribed) {
        const homeUrl = new URL(`${SWIPEY_BASE_URL}/`)
        homeUrl.searchParams.set('auth_token', authToken)
        homeUrl.searchParams.set('auth_type', 'login_pre_lander')
        homeUrl.searchParams.set('theme', ab_testing['createsexfriends-paywall-theme'])
        Object.entries(utm).forEach(([key, value]) => {
          if (value) homeUrl.searchParams.set(key, value)
        })
        if (isNewAccount) {
          homeUrl.searchParams.set('media', 'paywall-model-02.mp4')
          homeUrl.searchParams.set('pw_v', '1')
        }
        if (isPw) {
          homeUrl.searchParams.set('media', 'vid.mp4')
          homeUrl.searchParams.set('pw_v', '2')
        }
        if (isHalloween) {
          homeUrl.searchParams.set('media', 'vid.mp4')
        }
        console.log('homeUrl', homeUrl)
        return {
          success: true,
          redirectUrl: homeUrl.toString(),
        }
      } else {
        const paywallUrl = new URL(`${SWIPEY_BASE_URL}/paywall`)
        paywallUrl.searchParams.set('auth_token', authToken)
        paywallUrl.searchParams.set('auth_type', 'login_paywall')
        // Tell xDate paywall the user came from a lander and which chat to
        // redirect to after payment (`/chats/${selected_slug}`).
        paywallUrl.searchParams.set('from_landing', 'true')
        paywallUrl.searchParams.set('selected_slug', getSelectedCharacterSlug())
        paywallUrl.searchParams.set('theme', ab_testing['createsexfriends-paywall-theme'])

        if (selections?.welcome) {
          const typeValue = selections.welcome === 'Anime' ? 'anime' : 'realistic'
          paywallUrl.searchParams.set('type', typeValue)
        }

        Object.entries(utm).forEach(([key, value]) => {
          if (value) paywallUrl.searchParams.set(key, value)
        })

        // Intentionally NOT setting `model-params` — see comment in
        // paywall.js buildPaywallUrl. Sending model-params makes xDate
        // route to /generating-ai instead of /chats/{selected_slug}.

        if (isNewAccount) {
          paywallUrl.searchParams.set('media', 'paywall-model-02.mp4')
          paywallUrl.searchParams.set('pw_v', '1')
        }
        if (isPw) {
          paywallUrl.searchParams.set('media', 'vid.mp4')
          paywallUrl.searchParams.set('pw_v', '2')
        }
        if (isHalloween) {
          paywallUrl.searchParams.set('media', 'vid.mp4')
        }
        if (isAnnualPlan()) {
          paywallUrl.searchParams.set('plans', 'annual')
        }
        return {
          success: true,
          redirectUrl: paywallUrl.toString(),
        }
      }
    }

    return {
      success: false,
      message: 'Please check your email or password',
    }
  } catch (error) {
    return {
      success: false,
      message: 'Please check your email or password',
    }
  }
}

// ---- Magic-link / email-only OTP flow (createaitrans-magicLink = ON) ----

// Append model-params, theme, plans, and media/pw_v params (the same set the
// password flows build) onto the loginUrl the backend returned.
const decorateEmailOnlyRedirect = async (loginUrl, selections, ab_testing) => {
  if (!loginUrl) return null

  const url = new URL(loginUrl)

  // Tell xDate paywall the user came from a lander and which chat to
  // redirect to after payment (`/chats/${selected_slug}`).
  url.searchParams.set('from_landing', 'true')
  url.searchParams.set('selected_slug', getSelectedCharacterSlug())

  if (selections?.welcome) {
    url.searchParams.set('type', selections.welcome === 'Anime' ? 'anime' : 'realistic')
  }

  // Intentionally NOT setting `model-params` — see comment in paywall.js
  // buildPaywallUrl. Sending model-params makes xDate route to
  // /generating-ai instead of /chats/{selected_slug}.

  const theme = ab_testing?.['createaitrans-paywall-theme']
  if (theme) url.searchParams.set('theme', theme)

  const path = window.location.pathname
  if (path.includes('/newaccount')) {
    url.searchParams.set('media', 'paywall-model-02.mp4')
    url.searchParams.set('pw_v', '1')
  }
  if (path.includes('/pw')) {
    url.searchParams.set('media', 'vid.mp4')
    url.searchParams.set('pw_v', '2')
  }

  // /12 forces annual; otherwise honor GB redirect flag persisted at signup.
  const annualByRoute = isAnnualPlan()
  const annualByFlag = !annualByRoute && localStorage.getItem('postSignupRedirect') === 'annual'
  if (annualByRoute || annualByFlag) {
    url.searchParams.set('plans', 'annual')
  }

  return url.toString()
}

// POST /auth/pre-lander-with-email-only — used by both signup and login
// when the magic-link flag is ON. Returns either:
//   - otp_sent: true + otpExpiry  (existing email → caller shows OTP modal)
//   - authToken + redirectUrl     (new email → caller navigates directly)
export const submitEmailOnly = async (email, { isResend = false, selections, ab_testing } = {}) => {
  const utm = getStorage('utm_data') || {}
  const ef_transaction_id = getEfTransactionId()

  const payload = {
    ...utm,
    email,
    ef_transaction_id,
    ab_testing,
    isResend,
    lp_source: 'createaitrans',
  }

  const data = await apiRequest('/auth/pre-lander-with-email-only', 'POST', payload)

  if (data?.success === false) return data

  if (data?.authToken && data?.loginUrl) {
    const redirectUrl = await decorateEmailOnlyRedirect(data.loginUrl, selections, ab_testing)
    if (redirectUrl) data.redirectUrl = redirectUrl
    if (data.authToken) setAuth(data.authToken)
  }

  return data
}

// POST /auth/pre-lander/login-with-otp — verifies the OTP and returns the
// authToken + loginUrl (same shape as the auto-login branch of submitEmailOnly).
export const verifyPreLanderOtp = async (email, otp, { selections, ab_testing } = {}) => {
  const data = await apiRequest('/auth/pre-lander/login-with-otp', 'POST', { email, otp })

  if (data?.success === false) return data

  const authToken = data?.authToken || data?.token
  const loginUrl = data?.loginUrl

  if (authToken) setAuth(authToken)

  if (loginUrl) {
    const redirectUrl = await decorateEmailOnlyRedirect(loginUrl, selections, ab_testing)
    if (redirectUrl) data.redirectUrl = redirectUrl
  }

  return data
}

export const getGoogleAuthUrl = () => `${SWIPEY_BASE_URL}/api/v1/auth/google/login`

// Swipey base URL
export const getSwipeyBaseUrl = () => SWIPEY_BASE_URL

// Export API configuration for debugging
export const getApiConfig = () => ({
  baseUrl: API_BASE_URL,
  swipeyBaseUrl: SWIPEY_BASE_URL,
  googleAuthUrl: getGoogleAuthUrl(),
  hasApiKey: !!API_KEY && API_KEY !== 'your-api-key-here',
  attributesLoaded,
  attributesMap,
})

// ---- Add to your API service file ----

// Request password reset (send code)
export const requestPasswordReset = async email => {
  return apiRequest('/auth/reset-password', 'POST', { email })
}

// Verify OTP for password reset
export const verifyPasswordOtp = async (email, otp) => {
  return apiRequest('/auth/verify-reset-password-otp', 'POST', { email, otp })
}

// Set new password using token
export const updatePassword = async (token, newPassword) => {
  return apiRequest(`/auth/set-new-password?token=${token}`, 'POST', {
    newPassword,
  })
}
